/**
 * ExportServicesDialog — Projects service export (issue #647).
 *
 * Lets the user pick zero or more extra TagCategory columns, then downloads
 * one CSV row per current project service assignment. The required columns
 * (Surrogate ID, Team, Milestone, Three Rs Key, Service Name, Status,
 * Hosting Environment) are always included; the file is generated entirely
 * client-side via `exportToCSV` (Blob object-URL), so there is no relative
 * API URL / missing-auth-header download failure mode (issue #598) — the
 * data driving the export is already loaded on the page.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ProjectMilestone, ServiceAssignment, Tag, TagCategory } from '../../graphql/types';
import type { ServiceResolutionMap } from '../../lib/serviceResolution';
import {
  buildExportFilename,
  buildProjectExportRows,
  buildTagCategoriesByName,
  buildTagsById,
  isSelectableExportCategory,
} from '../../lib/projectExport';
import { exportToCSV } from '../../lib/exportUtils';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import '@tylertech/forge/checkbox';
import './ExportServicesDialog.css';

export interface ExportServicesDialogProps {
  open: boolean;
  projectName: string;
  assignments: ServiceAssignment[];
  milestones: ProjectMilestone[];
  serviceResolution: ServiceResolutionMap;
  tags: Tag[];
  tagCategories: TagCategory[];
  onClose: () => void;
}

const DialogElement = 'forge-dialog' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;
const ForgeCheckbox = 'forge-checkbox' as React.ElementType;

export function ExportServicesDialog({
  open,
  projectName,
  assignments,
  milestones,
  serviceResolution,
  tags,
  tagCategories,
  onClose,
}: ExportServicesDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open = open;
    }
    if (open) {
      setSelectedCategoryIds(new Set());
    }
  }, [open]);

  const selectableCategories = useMemo(
    () =>
      [...tagCategories]
        .filter(isSelectableExportCategory)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [tagCategories]
  );

  const toggleCategory = (categoryId: string): void => {
    setSelectedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleExport = (): void => {
    const tagsById = buildTagsById(tags);
    const categoriesByName = buildTagCategoriesByName(tagCategories);
    const selectedCategories = selectableCategories.filter((category) =>
      selectedCategoryIds.has(category.entityId)
    );

    const rows = buildProjectExportRows({
      assignments,
      milestones,
      serviceResolution,
      tagsById,
      categoriesByName,
      selectedCategories,
    });

    exportToCSV(rows, buildExportFilename(projectName));
    onClose();
  };

  const assignmentCountLabel = `${assignments.length} assigned service${assignments.length === 1 ? '' : 's'}`;

  return (
    <DialogElement
      ref={dialogRef}
      onForge-dialog-close={onClose}
      className="export-services-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="export-dialog-title"
    >
      <div className="dialog-content">
        <h2 id="export-dialog-title" className="forge-typography--headline5 export-dialog-title">
          Export Services
        </h2>

        <p className="forge-typography--body2 export-dialog-description">
          Exports {assignmentCountLabel} as CSV with Surrogate ID, Team, Milestone, Three Rs Key,
          Service Name, Status, and Hosting Environment columns. Optionally add more tag-group
          columns below.
        </p>

        {selectableCategories.length === 0 ? (
          <p className="forge-typography--caption export-dialog-empty">
            No additional tag groups are available to add as columns.
          </p>
        ) : (
          <div
            className="export-dialog-category-list"
            role="group"
            aria-label="Additional tag-group columns"
          >
            {selectableCategories.map((category) => (
              <label key={category.entityId} className="export-dialog-category-option">
                <ForgeCheckbox
                  checked={selectedCategoryIds.has(category.entityId)}
                  onChange={() => toggleCategory(category.entityId)}
                />
                {category.name}
              </label>
            ))}
          </div>
        )}

        <div className="dialog-actions">
          <ForgeButton type="button" variant="text" onClick={onClose}>
            Cancel
          </ForgeButton>
          <ForgeButton
            type="button"
            variant="raised"
            onClick={handleExport}
            disabled={assignments.length === 0}
          >
            Export CSV
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
